import { Fragment } from 'react'
import bodyStyles from '../article-body/ArticleBody.module.scss'
import { stringify } from 'qs'
import parse from 'html-react-parser'
import { useTranslation } from 'next-i18next'
import { ContentBlockModel } from 'types/cms/components'
import { Heading, Video, Img } from '@boltenergy-be/design-system'
import { ContentBlocksProps } from 'components/article/content-blocks/types'
import { ContentBlocks as Blocks } from 'types/cms/enums'

const ContentBlocks = ({ contentBlocks }: ContentBlocksProps) => {
  // i18n
  const { t } = useTranslation('encyclopedia')

  /**
   * calculates the font-size based on the amount of characters
   *
   * @param {string} quote
   * @returns string
   */
  const getQuoteFontSize = (quote: string): string => {
    if (quote?.length > 200) {
      return '18px'
    }

    if (quote?.length < 50) {
      return '28px'
    }

    return '24px'
  }

  /**
   * creates the url of the video
   * @param {ContentBlockModel} block
   * @returns string
   */
  const setVimeoUrl = (block: ContentBlockModel): string => {
    const hasParams = block?.autoplay || block?.loop
    const url = block?.url?.split('?')?.[0]

    const query = stringify(
      {
        autoplay: block?.autoplay ? 1 : null,
        loop: block?.loop ? 1 : null,
        muted: block?.autoplay ? 1 : null
      },
      { skipNulls: true }
    )

    return !hasParams ? url : `${url}?${query}`
  }

  return (
    <>
      {contentBlocks.map((block, idx) => {
        switch (block.__component) {
          case Blocks.RICH_TEXT:
            return (
              <div className={bodyStyles['rich-text']} key={`${block.__component}-${idx}`}>
                {parse(block.body)}
              </div>
            )
          case Blocks.IMAGE:
            return (
              <figure className={bodyStyles.image} key={`${block.__component}-${idx}`}>
                <Img
                  publicId={block?.image.data?.attributes.provider_metadata?.public_id}
                  alt={block?.image.data?.attributes.alternativeText}
                  width={block?.image.data?.attributes.width}
                  height={block?.image.data?.attributes.height}
                />
              </figure>
            )
          case Blocks.GOOD_TO_KNOW:
            return (
              <div key={`${block.__component}-${idx}`} className={bodyStyles['good-to-know']}>
                <Heading as="h4" variant="h5">
                  {t('goodToKnow', 'Goed om weten!')}
                </Heading>
                {parse(block.information)}
              </div>
            )
          case Blocks.QUOTE:
            return (
              <figure key={`${block.__component}-${idx}`} className={bodyStyles['quote-wrapper']}>
                <blockquote className={bodyStyles.quote} style={{ fontSize: getQuoteFontSize(block?.quote) }}>
                  &quot;{block?.quote}&quot;
                </blockquote>
                {block?.author && <figcaption>- {block.author}</figcaption>}
              </figure>
            )
          case Blocks.VIMEO:
            return (
              <iframe
                key={`${block.__component}-${idx}`}
                className={bodyStyles.vimeo}
                title={block?.title}
                src={setVimeoUrl(block)}
                allowFullScreen
                allow="autoplay"
              />
            )
          case Blocks.VIDEO:
            return (
              <Video
                key={`${block.__component}-${idx}`}
                className={bodyStyles.video}
                publicId={block.video.data.attributes.provider_metadata.public_id}
              />
            )
          default:
            return <Fragment key={`${block.__component}-${idx}`} />
        }
      })}
    </>
  )
}

export default ContentBlocks
